import { defineStore } from 'pinia';
import {
  fetchHomepage,
  fetchNewsDetail,
  fetchNewsPage,
  fetchProductCenter,
  fetchRecruitmentDetail,
  fetchRecruitmentsList,
  fetchRoutesList,
  fetchSolutionsList,
  fetchSuccessCasesList
} from '../api/front';
import type {
  FrontNewsDetail,
  FrontNewsPageData,
  FrontSuccessCaseItem,
  HomeSectionItem,
  FrontProductCenterData,
  JobItem,
  RouteMenuItem,
  SolutionItem
} from '../api/types/cms';
import { cmsCacheKey, hashCmsData, readCmsCache, readCmsCacheHash, writeCmsCache } from '../utils/cmsCache';
import {
  normalizeCmsList,
  normalizeFrontNewsPage,
  normalizeHomepage,
  normalizeProductCenter
} from '../utils/frontCmsNormalize';

export type FrontCmsKey =
  | 'routes'
  | 'homepage'
  | 'solutions'
  | 'productCenter'
  | 'recruitments'
  | 'newsPage'
  | 'successCases';

const DEFAULT_NEWS_PAGE_SIZE = 12;

type NewsPageState = FrontNewsPageData & {
  pageKey: string;
};

type FrontCmsState = {
  routes: RouteMenuItem[];
  homepage: Record<string, unknown> | null;
  solutions: SolutionItem[];
  productCenter: FrontProductCenterData | null;
  recruitments: JobItem[];
  newsPage: NewsPageState;
  successCases: FrontSuccessCaseItem[];
  newsDetails: Record<string, FrontNewsDetail | null>;
  recruitmentDetails: Record<string, JobItem | null>;
  hydrated: boolean;
  syncing: Partial<Record<FrontCmsKey | 'newsDetail' | 'recruitmentDetail', boolean>>;
};

const CACHE_KEYS: Record<FrontCmsKey, string> = {
  routes: cmsCacheKey('routes'),
  homepage: cmsCacheKey('homepage'),
  solutions: cmsCacheKey('solutions'),
  productCenter: cmsCacheKey('product-center'),
  recruitments: cmsCacheKey('recruitments'),
  newsPage: cmsCacheKey('news', 'page'),
  successCases: cmsCacheKey('success-cases')
};

const FETCHERS: Record<FrontCmsKey, () => Promise<unknown>> = {
  routes: fetchRoutesList,
  homepage: fetchHomepage,
  solutions: fetchSolutionsList,
  productCenter: fetchProductCenter,
  recruitments: fetchRecruitmentsList,
  newsPage: () => fetchNewsPage({ pageNum: 1, pageSize: DEFAULT_NEWS_PAGE_SIZE }),
  successCases: fetchSuccessCasesList
};

const inflight = new Map<string, Promise<void>>();

export const useFrontCmsStore = defineStore('frontCms', {
  state: (): FrontCmsState => ({
    routes: [],
    homepage: null,
    solutions: [],
    productCenter: null,
    recruitments: [],
    newsPage: {
      records: [],
      total: 0,
      size: DEFAULT_NEWS_PAGE_SIZE,
      current: 1,
      pages: 1,
      pageKey: '1:12'
    },
    successCases: [],
    newsDetails: {},
    recruitmentDetails: {},
    hydrated: false,
    syncing: {}
  }),

  getters: {
    homeBanners(state): HomeSectionItem[] {
      if (!state.homepage) {
        return [];
      }
      const banners = state.homepage.banners ?? state.homepage.bannerList;
      return Array.isArray(banners) ? (banners as HomeSectionItem[]) : [];
    },

    enabledRoutes(state): RouteMenuItem[] {
      return [...state.routes]
        .filter((r) => r.enabled !== 0)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    }
  },

  actions: {
    hydrateFromCache() {
      if (this.hydrated) {
        return;
      }

      (Object.keys(CACHE_KEYS) as FrontCmsKey[]).forEach((key) => {
        const cached = readCmsCache<unknown>(CACHE_KEYS[key]);
        if (cached !== null) {
          this.applyKey(key, cached);
        }
      });

      this.hydrated = true;
    },

    applyKey(key: FrontCmsKey, raw: unknown): boolean {
      switch (key) {
        case 'routes':
          return this.setIfChanged('routes', normalizeCmsList<RouteMenuItem>(raw));
        case 'homepage':
          return this.setIfChanged('homepage', normalizeHomepage(raw));
        case 'solutions':
          return this.setIfChanged('solutions', normalizeCmsList<SolutionItem>(raw));
        case 'productCenter':
          return this.setIfChanged('productCenter', normalizeProductCenter(raw));
        case 'recruitments':
          return this.setIfChanged('recruitments', normalizeCmsList<JobItem>(raw));
        case 'newsPage':
          return this.setNewsPageState(normalizeFrontNewsPage(raw), '1:12');
        case 'successCases':
          return this.setIfChanged('successCases', normalizeCmsList<FrontSuccessCaseItem>(raw));
        default:
          return false;
      }
    },

    setNewsPageState(data: FrontNewsPageData, pageKey: string): boolean {
      const next: NewsPageState = { ...data, pageKey };
      const prevPayload = {
        records: this.newsPage.records,
        total: this.newsPage.total,
        size: this.newsPage.size,
        current: this.newsPage.current,
        pages: this.newsPage.pages
      };
      if (hashCmsData(prevPayload) === hashCmsData(data)) {
        if (this.newsPage.pageKey !== pageKey) {
          this.newsPage = next;
        }
        return false;
      }
      this.newsPage = next;
      return true;
    },

    newsPageCacheKey(pageNum: number, pageSize: number) {
      return cmsCacheKey('news', 'page', String(pageNum), String(pageSize));
    },

    async syncNewsPage(pageNum = 1, pageSize = DEFAULT_NEWS_PAGE_SIZE) {
      const pageKey = `${pageNum}:${pageSize}`;
      const cacheStorageKey = this.newsPageCacheKey(pageNum, pageSize);
      const taskKey = `newsPage:${pageKey}`;
      const existing = inflight.get(taskKey);
      if (existing) {
        return existing;
      }

      const cached = readCmsCache<unknown>(cacheStorageKey);
      if (cached !== null) {
        this.setNewsPageState(normalizeFrontNewsPage(cached), pageKey);
      }

      const task = (async () => {
        this.syncing.newsPage = true;
        try {
          const raw = await fetchNewsPage({ pageNum, pageSize });
          const cachedHash = readCmsCacheHash(cacheStorageKey);
          const freshHash = hashCmsData(raw);

          if (cachedHash === freshHash) {
            this.setNewsPageState(normalizeFrontNewsPage(raw), pageKey);
            return;
          }

          this.setNewsPageState(normalizeFrontNewsPage(raw), pageKey);
          writeCmsCache(cacheStorageKey, raw);
        } catch (error) {
          console.warn(`[frontCms] 同步新闻分页 ${pageKey} 失败`, error);
        } finally {
          this.syncing.newsPage = false;
        }
      })();

      inflight.set(taskKey, task);
      return task.finally(() => inflight.delete(taskKey));
    },

    setIfChanged<K extends keyof FrontCmsState>(field: K, next: FrontCmsState[K]): boolean {
      if (hashCmsData(this[field]) === hashCmsData(next)) {
        return false;
      }
      this.$patch({ [field]: next } as Pick<FrontCmsState, K>);
      return true;
    },

    async ensure(key: FrontCmsKey) {
      return this.sync(key);
    },

    async ensureMany(keys: FrontCmsKey[]) {
      await Promise.all(keys.map((key) => this.sync(key)));
    },

    async refreshAll() {
      const keys = Object.keys(CACHE_KEYS) as FrontCmsKey[];
      await this.ensureMany(keys);
    },

    async sync(key: FrontCmsKey) {
      const taskKey = `sync:${key}`;
      const existing = inflight.get(taskKey);
      if (existing) {
        return existing;
      }

      const task = this.syncKey(key).finally(() => {
        inflight.delete(taskKey);
      });
      inflight.set(taskKey, task);
      return task;
    },

    async syncKey(key: FrontCmsKey) {
      this.syncing[key] = true;
      try {
        const raw = await FETCHERS[key]();
        const cacheStorageKey = CACHE_KEYS[key];
        const cachedHash = readCmsCacheHash(cacheStorageKey);
        const freshHash = hashCmsData(raw);

        if (cachedHash === freshHash) {
          this.applyKey(key, raw);
          return;
        }

        this.applyKey(key, raw);
        writeCmsCache(cacheStorageKey, raw);
      } catch (error) {
        console.warn(`[frontCms] 同步 ${key} 失败`, error);
      } finally {
        this.syncing[key] = false;
      }
    },

    async syncNewsDetail(id: string) {
      if (!id) {
        return null;
      }

      const cacheKey = cmsCacheKey('news', 'detail', id);
      const taskKey = `newsDetail:${id}`;
      const existing = inflight.get(taskKey);
      if (existing) {
        await existing;
        return this.newsDetails[id] ?? null;
      }

      const cached = readCmsCache<FrontNewsDetail>(cacheKey);
      if (cached) {
        this.newsDetails[id] = cached;
      }

      const task = (async () => {
        this.syncing.newsDetail = true;
        try {
          const raw = await fetchNewsDetail(id);
          const cachedHash = readCmsCacheHash(cacheKey);
          const freshHash = hashCmsData(raw);

          if (cachedHash === freshHash) {
            if (!this.newsDetails[id]) {
              this.newsDetails[id] = raw;
            }
            return;
          }

          const current = this.newsDetails[id];
          if (!current || hashCmsData(current) !== freshHash) {
            this.newsDetails[id] = raw;
          }
          writeCmsCache(cacheKey, raw);
        } catch (error) {
          console.warn(`[frontCms] 同步新闻详情 ${id} 失败`, error);
        } finally {
          this.syncing.newsDetail = false;
        }
      })();

      inflight.set(taskKey, task);
      await task.finally(() => inflight.delete(taskKey));
      return this.newsDetails[id] ?? null;
    },

    getNewsDetail(id: string): FrontNewsDetail | null {
      return this.newsDetails[id] ?? readCmsCache<FrontNewsDetail>(cmsCacheKey('news', 'detail', id));
    },

    async syncRecruitmentDetail(id: string) {
      if (!id) {
        return null;
      }

      const cacheKey = cmsCacheKey('recruitments', 'detail', id);
      const taskKey = `recruitmentDetail:${id}`;
      const existing = inflight.get(taskKey);
      if (existing) {
        await existing;
        return this.recruitmentDetails[id] ?? null;
      }

      const cached = readCmsCache<JobItem>(cacheKey);
      if (cached) {
        this.recruitmentDetails[id] = cached;
      }

      const task = (async () => {
        this.syncing.recruitmentDetail = true;
        try {
          const raw = await fetchRecruitmentDetail(id);
          const cachedHash = readCmsCacheHash(cacheKey);
          const freshHash = hashCmsData(raw);

          if (cachedHash === freshHash) {
            if (!this.recruitmentDetails[id]) {
              this.recruitmentDetails[id] = raw;
            }
            return;
          }

          const current = this.recruitmentDetails[id];
          if (!current || hashCmsData(current) !== freshHash) {
            this.recruitmentDetails[id] = raw;
          }
          writeCmsCache(cacheKey, raw);
        } catch (error) {
          console.warn(`[frontCms] 同步招聘详情 ${id} 失败`, error);
        } finally {
          this.syncing.recruitmentDetail = false;
        }
      })();

      inflight.set(taskKey, task);
      await task.finally(() => inflight.delete(taskKey));
      return this.recruitmentDetails[id] ?? null;
    },

    async syncRecruitmentDetails(ids: string[]) {
      const unique = [...new Set(ids.filter(Boolean))];
      await Promise.all(unique.map((id) => this.syncRecruitmentDetail(id)));
    },

    getRecruitmentDetail(id: string): JobItem | null {
      return (
        this.recruitmentDetails[id] ?? readCmsCache<JobItem>(cmsCacheKey('recruitments', 'detail', id))
      );
    }
  }
});
